import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import {
  setMessage, userLogin, restPasswordForUser, registerUserAccount,
  // loginAs,
  confirmUserAccount, changePasswordByUser, getNewCaptcha, setUser,
  registerFailedUserAccountSuccess, registerFailedUserAccountError, registerUserAccountInitiated
} from './user.actions'

import { ApiError } from '../loginRoot.reducer'
import { Message, Success } from '@professocial/professocialTypes/types/messageTypes'
import { CaptchaSolution } from '@professocialApi/professocial-core-frontend-api'

export interface UserState {
  fetching: boolean
  onRegistration: boolean
  failedRegistrationSuccess: boolean // Neue State-Eigenschaft
  failedRegistrationError?: any // Neue State-Eigenschaft
  user?: any
  error: ApiError
  success: Success
  message?: Message
  captcha?: CaptchaSolution
  redirectPath?: string
  isLogged: boolean
}

const INITIAL_STATE: UserState = {
  fetching: false,
  onRegistration: false,
  isLogged: false,
  error: {},
  success: {},
  failedRegistrationSuccess: false, // Initial false
}

const triggerProfessocialMessage = (data: Message) => {
  window.dispatchEvent(new CustomEvent('ProfessocialMessage', { detail: data }))
}

export const userReducer = reducerWithInitialState(INITIAL_STATE)
  /**
   * userLogin
   */
  .case(userLogin.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.userLogin
    })
  })
  .case(userLogin.done, (state, { result }) => {
    return produce(state, draft => {
      draft.isLogged = true
      result.id && window.localStorage.setItem('user', result.id)
      window.dispatchEvent(new CustomEvent('ProfessocialUser', { detail: result }))
      draft.user = result
      draft.fetching = false
    })
  })
  .case(userLogin.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.userLogin = errorStatus
    })
  })

  /**
   * restPasswordForUser
   */
  .case(restPasswordForUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.restPasswordForUser
    })
  })
  .case(restPasswordForUser.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })
  .case(restPasswordForUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.restPasswordForUser = (error as Response)
    })
  })

  /**
   * registerUserAccount
   */
  .case(registerUserAccountInitiated, state => {
    return produce(state, draft => {
      draft.failedRegistrationSuccess = false // Zurücksetzen bei neuem Versuch
      draft.failedRegistrationError = undefined // Zurücksetzen bei neuem Versuch
    })
  })
  .case(registerUserAccount.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.onRegistration = true
      delete draft.error.registerUserAccount
    })
  })
  .case(registerUserAccount.done, (state, { result }) => {
    return produce(state, draft => {
      draft.isLogged = true
      draft.user = result
      draft.fetching = false
      draft.onRegistration = false
      draft.failedRegistrationSuccess = true // Bei Erfolg setzen
    })
  })
  .case(registerUserAccount.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.onRegistration = false
      draft.failedRegistrationSuccess = false // Bei Fehler nicht setzen
      draft.failedRegistrationError = error // Fehler speichern
      draft.error.registerUserAccount = (error as Response)
    })
  })

  /**
   * Neue Cases für den Erfolg/Fehler der zweiten Registrierung
   */
  .case(registerFailedUserAccountSuccess, state => {
    return produce(state, draft => {
      draft.failedRegistrationSuccess = true
    })
  })
  .case(registerFailedUserAccountError, (state, error) => {
    return produce(state, draft => {
      draft.failedRegistrationSuccess = false
      draft.failedRegistrationError = error
    })
  })

/**
   * getNewCaptcha
   */
  .case(getNewCaptcha.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getNewCaptcha
    })
  })
  .case(getNewCaptcha.done, (state, { result }) => {
    return produce(state, draft => {
      draft.captcha = result
      draft.fetching = false
    })
  })
  .case(getNewCaptcha.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getNewCaptcha = (error as Response)
    })
  })

  /**
   * confirmUserAccount
   */
  .case(confirmUserAccount.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.confirmUserAccount
    })
  })
  .case(confirmUserAccount.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })
  .case(confirmUserAccount.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.confirmUserAccount = (error as Response)
    })
  })

  /**
   * changePasswordByUser
   */
  .case(changePasswordByUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.changePasswordByUser
    })
  })
  .case(changePasswordByUser.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })
  .case(changePasswordByUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.changePasswordByUser = (error as Response)
    })
  })

  /**
   * Message Management
   */
  .case(setMessage, (state, message) => {
    // Fix - wait while the reducer is executing
    setTimeout(() => triggerProfessocialMessage(message))

    return produce(state, (draft) => {
      draft.message = message
    })
  })

  .case(setUser, (state, user) => {
    return ({ ...state, user })
  })
